/*
 * Cactus Jake — Collection grid controller
 * ------------------------------------------------------------
 * Adds two shopper-selectable display modes to every collection page:
 *
 *   - infinite : products load in batches as the shopper scrolls
 *                (default; also exposes a keyboard-accessible "Load more").
 *   - paged    : products are sliced into numbered pages of 20 / 36 / 48.
 *
 * The choice lives in localStorage and is changed from the gear menu
 * rendered by main-collection-product-grid.liquid.
 *
 * The section renders the first server batch normally (Liquid {% paginate %}).
 * Additional batches are fetched with the Shopify Section Rendering API
 * (?section_id=...&page=N), the same mechanism Dawn's facets.js uses, so
 * filtering keeps working — when Dawn replaces #ProductGridContainer this
 * controller re-initialises from the fresh grid via a MutationObserver.
 *
 * Native {% render 'pagination' %} is left in the markup as the no-JS /
 * crawler fallback and hidden with CSS once this controller is active.
 */
(function () {
  'use strict';

  var MODE_KEY = 'cj:grid-mode';
  var PERPAGE_KEY = 'cj:grid-perpage';
  var DEFAULT_MODE = 'infinite';
  var DEFAULT_PERPAGE = 20;
  var PERPAGE_OPTIONS = [20, 36, 48];
  var CHEVRON =
    '<svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true" focusable="false">' +
    '<path d="M8.5 1.5 2 8l6.5 6.5" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function lsGet(key, fallback) {
    try {
      var v = window.localStorage.getItem(key);
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }
  function lsSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode / storage full — preference simply won't persist */
    }
  }

  function prefMode() {
    return lsGet(MODE_KEY, DEFAULT_MODE) === 'paged' ? 'paged' : 'infinite';
  }
  function prefPerPage() {
    var n = parseInt(lsGet(PERPAGE_KEY, DEFAULT_PERPAGE), 10);
    return PERPAGE_OPTIONS.indexOf(n) >= 0 ? n : DEFAULT_PERPAGE;
  }

  /* --------------------------------------------------------------- */

  function CollectionGrid(section) {
    this.section = section;
    this.container = section.querySelector('#ProductGridContainer');
    this.toolbar = section.querySelector('[data-cj-toolbar]');
    this.footer = section.querySelector('[data-cj-grid-footer]');
    if (!this.container || !this.footer) return;

    this.grid = null;
    this.io = null;
    this.moreEl = null;
    this.moreBtn = null;
    this.currentPage = 0;
    this.serverPagesLoaded = 1;
    this.serverPageSize = DEFAULT_PERPAGE;
    this.total = 0;
    this.busy = false;

    this.bindGear();
    this.dockGear();
    this.bindContainerObserver();
    this.init();
  }

  CollectionGrid.prototype.items = function () {
    return this.grid
      ? Array.prototype.slice.call(this.grid.querySelectorAll(':scope > .grid__item'))
      : [];
  };

  CollectionGrid.prototype.hasMoreServer = function () {
    return this.items().length < this.total;
  };

  /* (Re)bind to the grid — runs on first load and after every facet AJAX. */
  CollectionGrid.prototype.init = function () {
    this.teardownSentinel();
    this.grid = this.container.querySelector('#product-grid');
    this.currentPage = 0;
    this.serverPagesLoaded = 1;
    this.busy = false;

    if (!this.grid) {
      // Empty / "coming soon" result set.
      this.footer.innerHTML = '';
      if (this.toolbar) this.toolbar.hidden = true;
      return;
    }

    this.sectionId = this.grid.dataset.id || '';
    this.serverPageSize = parseInt(this.grid.dataset.cjServerPageSize, 10) || DEFAULT_PERPAGE;
    this.total = parseInt(this.grid.dataset.cjTotal, 10) || this.items().length;

    // Mode/per-page only matter once there is more than the smallest page.
    if (this.toolbar) this.toolbar.hidden = this.total <= PERPAGE_OPTIONS[0];

    this.container.classList.add('cj-js-grid'); // CSS hides native pagination
    this.render();
  };

  CollectionGrid.prototype.render = function () {
    if (!this.grid) return;
    this.syncGearControls();
    if (prefMode() === 'paged') {
      this.applyPaged();
    } else {
      this.applyInfinite();
    }
  };

  /* ----------------------------- infinite ------------------------ */

  CollectionGrid.prototype.applyInfinite = function () {
    this.items().forEach(function (li) {
      li.classList.remove('cj-hide');
    });
    this.footer.innerHTML = '';
    this.teardownSentinel();
    if (!this.hasMoreServer()) return;

    var wrap = document.createElement('div');
    wrap.className = 'cj-grid-more';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cj-grid-more__btn';
    btn.textContent = 'Load more';
    btn.addEventListener('click', this.loadMore.bind(this));

    wrap.appendChild(btn);
    this.footer.appendChild(wrap);
    this.moreBtn = btn;
    this.moreEl = wrap;
    this.setupSentinel();
  };

  /* Observe the (visible) load-more wrapper so scrolling near it auto-loads.
     Throttled + narrowed rootMargin so a fast-scroll fling doesn't stack
     back-to-back fetches faster than the browser can GC prior parse
     documents and image decode buffers. iOS Safari on a fast scroll would
     otherwise pile up 3-4 pending fetches + parsed detached DOMs + fresh
     image decodes and kill the process. Slow-scroll UX is unchanged. */
  CollectionGrid.prototype.setupSentinel = function () {
    if (!this.moreEl || !('IntersectionObserver' in window)) return;
    var self = this;
    var lastAutoLoad = 0;
    var AUTOLOAD_COOLDOWN_MS = 500;
    this.io = new IntersectionObserver(
      function (entries) {
        if (entries[0] && entries[0].isIntersecting) {
          var now = Date.now();
          if (now - lastAutoLoad < AUTOLOAD_COOLDOWN_MS) return;
          lastAutoLoad = now;
          self.loadMore();
        }
      },
      { rootMargin: '300px 0px' }
    );
    this.io.observe(this.moreEl);
  };

  CollectionGrid.prototype.teardownSentinel = function () {
    if (this.io) {
      this.io.disconnect();
      this.io = null;
    }
    this.moreEl = null;
    this.moreBtn = null;
  };

  CollectionGrid.prototype.loadMore = function () {
    var self = this;
    if (this.busy || prefMode() !== 'infinite' || !this.hasMoreServer()) return;
    this.busy = true;
    if (this.moreBtn) {
      this.moreBtn.disabled = true;
      this.moreBtn.textContent = 'Loading…';
    }
    this.fetchAndAppend(this.serverPagesLoaded + 1)
      .catch(function (e) {
        console.warn('[cj-grid] load more failed', e);
      })
      .then(function () {
        self.busy = false;
        if (prefMode() !== 'infinite' || !self.grid) return;
        self.items().forEach(function (li) {
          li.classList.remove('cj-hide');
        });
        if (self.hasMoreServer()) {
          if (self.moreBtn) {
            self.moreBtn.disabled = false;
            self.moreBtn.textContent = 'Load more';
          }
          // Re-arm the observer so a still-visible wrapper keeps loading.
          if (self.io && self.moreEl) {
            self.io.unobserve(self.moreEl);
            self.io.observe(self.moreEl);
          }
        } else {
          self.footer.innerHTML = '';
          self.teardownSentinel();
        }
      });
  };

  /* ------------------------------ paged -------------------------- */

  CollectionGrid.prototype.applyPaged = function () {
    var self = this;
    var perPage = prefPerPage();
    var totalPages = Math.max(1, Math.ceil(this.total / perPage));
    if (this.currentPage >= totalPages) this.currentPage = totalPages - 1;
    if (this.currentPage < 0) this.currentPage = 0;

    this.teardownSentinel();
    var needed = Math.min(this.total, (this.currentPage + 1) * perPage);

    var finish = function () {
      if (prefMode() !== 'paged' || !self.grid) return;
      var start = self.currentPage * perPage;
      var end = start + perPage;
      self.items().forEach(function (li, i) {
        li.classList.toggle('cj-hide', i < start || i >= end);
      });
      self.renderPager(totalPages);
    };

    if (this.items().length >= needed || !this.hasMoreServer()) {
      finish();
      return;
    }

    this.footer.innerHTML =
      '<div class="cj-grid-loading"><span class="cj-grid-spinner"></span></div>';
    this.busy = true;
    this.ensureLoaded(needed)
      .catch(function (e) {
        console.warn('[cj-grid] page fetch failed', e);
      })
      .then(function () {
        self.busy = false;
        finish();
      });
  };

  CollectionGrid.prototype.ensureLoaded = function (count) {
    var self = this;
    function step() {
      if (self.items().length >= count || !self.hasMoreServer()) {
        return Promise.resolve();
      }
      return self.fetchAndAppend(self.serverPagesLoaded + 1).then(function (n) {
        if (n === 0) return;
        return step();
      });
    }
    return step();
  };

  CollectionGrid.prototype.renderPager = function (totalPages) {
    this.footer.innerHTML = '';
    if (totalPages <= 1) return;

    var self = this;
    var cur = this.currentPage;
    var ul = document.createElement('ul');
    ul.className = 'pagination__list list-unstyled';
    ul.setAttribute('role', 'list');

    var addArrow = function (page, kind) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className =
        'pagination__item pagination__item--' + kind + ' pagination__item-arrow link';
      a.href = '#';
      a.setAttribute('aria-label', kind === 'prev' ? 'Previous page' : 'Next page');
      a.innerHTML = '<span class="cj-pager__chev cj-pager__chev--' + kind + '">' + CHEVRON + '</span>';
      a.addEventListener('click', function (e) {
        e.preventDefault();
        self.goToPage(page);
      });
      li.appendChild(a);
      ul.appendChild(li);
    };
    var addNumber = function (page) {
      var li = document.createElement('li');
      if (page === cur) {
        var span = document.createElement('a');
        span.className = 'pagination__item pagination__item--current';
        span.setAttribute('aria-current', 'page');
        span.textContent = String(page + 1);
        li.appendChild(span);
      } else {
        var a = document.createElement('a');
        a.className = 'pagination__item link';
        a.href = '#';
        a.textContent = String(page + 1);
        a.setAttribute('aria-label', 'Page ' + (page + 1));
        a.addEventListener('click', function (e) {
          e.preventDefault();
          self.goToPage(page);
        });
        li.appendChild(a);
      }
      ul.appendChild(li);
    };
    var addGap = function () {
      var li = document.createElement('li');
      var span = document.createElement('span');
      span.className = 'pagination__item';
      span.textContent = '…';
      li.appendChild(span);
      ul.appendChild(li);
    };

    // Windowed page list: first, last, and a window around the current page.
    var pages = [];
    for (var p = 0; p < totalPages; p++) {
      if (p === 0 || p === totalPages - 1 || (p >= cur - 1 && p <= cur + 1)) {
        pages.push(p);
      }
    }

    if (cur > 0) addArrow(cur - 1, 'prev');
    var prev = -1;
    pages.forEach(function (p) {
      if (prev !== -1 && p - prev > 1) addGap();
      addNumber(p);
      prev = p;
    });
    if (cur < totalPages - 1) addArrow(cur + 1, 'next');

    var nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Pagination');
    nav.appendChild(ul);

    var wrap = document.createElement('div');
    wrap.className = 'pagination-wrapper cj-pager';
    wrap.appendChild(nav);
    this.footer.appendChild(wrap);
  };

  CollectionGrid.prototype.goToPage = function (page) {
    if (this.busy) return;
    this.currentPage = page;
    this.applyPaged();
    var top = this.toolbar || this.section;
    if (top && top.scrollIntoView) {
      top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /* ---------------------------- fetching ------------------------- */

  CollectionGrid.prototype.fetchAndAppend = function (pageNum) {
    var self = this;
    return this.fetchServerPage(pageNum).then(function (newItems) {
      if (newItems.length === 0) {
        /* Defensive stop: the server returned an empty page but hasMoreServer()
           still says more exists. This can happen when this.total (from
           collection.products_count on initial render) disagrees with what
           paginate serves — e.g. a product went out of stock between page
           load and this fetch, or the initial count was cached and stale.
           Clamp total to what's actually in the DOM so hasMoreServer()
           returns false and the load-more sentinel is torn down. Without
           this, IntersectionObserver keeps firing and we spam empty fetches. */
        self.total = self.items().length;
        return 0;
      }
      newItems.forEach(function (li) {
        self.grid.appendChild(li);
      });
      self.serverPagesLoaded = Math.max(self.serverPagesLoaded, pageNum);
      return newItems.length;
    });
  };

  CollectionGrid.prototype.fetchServerPage = function (pageNum) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.set('section_id', this.sectionId);
    params.set('page', String(pageNum));
    var url = window.location.pathname + '?' + params.toString();

    return fetch(url)
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var newGrid = doc.getElementById('product-grid');
        var items = newGrid
          ? Array.prototype.slice.call(newGrid.querySelectorAll(':scope > .grid__item'))
          : [];
        /* Break references to the parsed detached document so GC can reclaim
           it before the next fetch. Otherwise retained closures on parsed
           DOM elements pin megabytes of detached tree in memory until the
           next major collection — which iOS Safari may not run in time on
           a fast-scroll session. */
        newGrid = null;
        doc = null;
        text = null;
        return items;
      });
  };

  /* Move the gear toolbar up into Dawn's filter bar (aside#main-collection-filters)
     and re-order the row to [filters … sort] [gear] [count]: the desktop product
     count is pulled out of the facet form and re-appended after the gear. The
     gear stays outside #FacetFiltersForm, so its inputs never pollute the facet
     query; the count keeps its #ProductCountDesktop id, so Dawn still refreshes
     it after facet AJAX. The aside is never rebuilt by Dawn — runs once. */
  CollectionGrid.prototype.dockGear = function () {
    if (!this.toolbar) return;
    var aside = this.section.querySelector('#main-collection-filters');
    if (!aside) return;
    if (!aside.contains(this.toolbar)) {
      this.toolbar.classList.remove('page-width');
      this.toolbar.classList.add('cj-toolbar--docked');
      aside.appendChild(this.toolbar);
    }
    var form = this.section.querySelector('#FacetFiltersForm');
    var count = form ? form.querySelector('.product-count') : null;
    if (count && count.parentElement !== aside) {
      count.classList.add('cj-count-docked', 'small-hide');
      aside.appendChild(count);
    }
  };

  /* ----------------------------- gear ---------------------------- */

  CollectionGrid.prototype.bindGear = function () {
    if (!this.toolbar) return;
    var self = this;
    this.modeInputs = this.toolbar.querySelectorAll('input[name="cj-grid-mode"]');
    this.perPageSelect = this.toolbar.querySelector('[data-cj-perpage]');
    this.gearDetails = this.toolbar.querySelector('details.cj-gear');

    Array.prototype.forEach.call(this.modeInputs, function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        lsSet(MODE_KEY, input.value === 'paged' ? 'paged' : 'infinite');
        self.currentPage = 0;
        self.updateGearState();
        self.render();
      });
    });
    if (this.perPageSelect) {
      this.perPageSelect.addEventListener('change', function () {
        lsSet(PERPAGE_KEY, self.perPageSelect.value);
        self.currentPage = 0;
        if (prefMode() === 'paged') self.render();
      });
    }
    // Close the gear popover on outside click.
    document.addEventListener('click', function (e) {
      if (self.gearDetails && self.gearDetails.open && !self.gearDetails.contains(e.target)) {
        self.gearDetails.open = false;
      }
    });
    this.syncGearControls();
    this.updateGearState();
  };

  /* Reflect stored prefs onto the gear controls. */
  CollectionGrid.prototype.syncGearControls = function () {
    if (!this.toolbar) return;
    var mode = prefMode();
    Array.prototype.forEach.call(this.modeInputs || [], function (input) {
      input.checked = input.value === mode;
    });
    if (this.perPageSelect) this.perPageSelect.value = String(prefPerPage());
  };

  /* Dim the per-page selector when it has no effect (infinite mode). */
  CollectionGrid.prototype.updateGearState = function () {
    if (!this.toolbar) return;
    var paged = prefMode() === 'paged';
    this.toolbar.classList.toggle('cj-toolbar--paged', paged);
    if (this.perPageSelect) this.perPageSelect.disabled = !paged;
  };

  /* --------------------- facet-AJAX re-binding ------------------- */

  CollectionGrid.prototype.bindContainerObserver = function () {
    var self = this;
    if (!('MutationObserver' in window)) return;
    // childList only (no subtree): fires when Dawn swaps #ProductGridContainer
    // innerHTML, but NOT when this controller appends <li> to #product-grid.
    this.mutationObs = new MutationObserver(function () {
      self.init();
    });
    this.mutationObs.observe(this.container, { childList: true });
  };

  /* ------------------------------ boot --------------------------- */

  function boot() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    var section = container.closest('[id^="shopify-section-"]');
    if (!section || section.cjGrid) return;
    section.cjGrid = new CollectionGrid(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor: re-boot when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target;
    if (section && section.querySelector && section.querySelector('#ProductGridContainer')) {
      section.cjGrid = null;
      boot();
    }
  });
})();
